import React, { useMemo, useEffect } from 'react';
import BaseMap from './BaseMap';
import { scaleLinear } from 'd3-scale';
import { Box } from '@mui/material';
import { formatPercentage } from '../utils/mapUtils';
import { fipsToStateName } from '../utils/fipsUtils';

const PovertyRateMap = ({ data }) => {
  // Debug on mount
  useEffect(() => {
    console.log('PovertyRateMap data received:', data?.length || 0, 'items');
    console.log('Poverty rate range:', 
      Math.min(...data.map(d => d.povertyRate).filter(r => r !== undefined && !isNaN(r))),
      'to',
      Math.max(...data.map(d => d.povertyRate).filter(r => r !== undefined && !isNaN(r))));
    
    // Log some examples of counties with high poverty rates
    const examples = data
      ?.filter(d => d.povertyRate !== undefined && !isNaN(d.povertyRate))
      ?.sort((a, b) => b.povertyRate - a.povertyRate)
      ?.slice(0, 5) || [];
    
    if (examples.length > 0) {
      console.log('Examples of high poverty counties:', 
        examples.map(d => `${d.countyName}, ${d.stateCode} (${formatPercentage(d.povertyRate)})`));
    }
  }, [data]);

  // Convert data to a consistent format with normalized FIPS codes
  const processedData = useMemo(() => {
    return data?.map(item => ({
      fips: String(item.fips).padStart(5, '0'),
      povertyRate: item.povertyRate,
      countyName: item.countyName,
      stateCode: item.stateCode
    })) || [];
  }, [data]);

  // Continuous color scale for poverty rate
  const colorScale = useMemo(() => 
    scaleLinear()
      .domain([20, 25, 30, 35, 40, 50])  // Poverty rate percentages
      .range(['#EEEEEE', '#e5f5e0', '#a1d99b', '#31a354', '#ff9b00', '#de2d26']), // Green to yellow to red
  []);

  // Update getFillColor to use direct matching first, then fallback
  const getFillColor = (geo, directMatch = null) => {
    // If we already have a direct match from BaseMap's matching logic, use it
    if (directMatch) {
      return directMatch.povertyRate !== undefined && !isNaN(directMatch.povertyRate)
        ? colorScale(directMatch.povertyRate)
        : '#EEEEEE';
    }
    
    // Otherwise, do the matching here
    const fips = String(geo.id).padStart(5, '0');
    const countyData = processedData.find(d => d.fips === fips);
    
    return countyData?.povertyRate !== undefined && !isNaN(countyData.povertyRate)
      ? colorScale(countyData.povertyRate)
      : '#EEEEEE';
  };

  // Tooltip handler showing poverty rate percentage
  const handleMouseMove = (event, geo, countyData) => {
    const countyName = geo.properties?.name || 'Unknown';
    const stateFips = geo.id ? String(geo.id).substring(0, 2) : '';
    const stateName = fipsToStateName[stateFips] || '';
    
    // Check if we have a valid poverty rate
    if (!countyData?.povertyRate) return 'No data available';
    
    // Format the percentage correctly based on its magnitude
    const formattedRate = countyData.povertyRate > 1 ? 
      `${Math.round(countyData.povertyRate * 10) / 10}%` : 
      `${Math.round(countyData.povertyRate * 1000) / 10}%`;
    
    return `
      <strong>${countyName} County${stateName ? `, ${stateName}` : ''}</strong><br/>
      Poverty Rate: ${formattedRate}
    `;
  };

  // Add data validation
  useEffect(() => {
    console.log('[PovertyRateMap] Processed data sample:', {
      inputFips: data?.[0]?.fips,
      processedFips: processedData?.[0]?.fips,
      geoFipsExample: "46121",
      match: processedData?.some(d => d.fips === "46121"),
      examplePovertyRate: processedData?.find(d => d.fips === "46121")?.povertyRate
    });
  }, [processedData]);

  // Create legend data
  const legendColors = useMemo(() => 
    ['#e5f5e0', '#a1d99b', '#31a354', '#ff9b00', '#de2d26'],
  []);

  const legendValues = useMemo(() => [
    '0-10%',
    '10-20%',
    '20-30%',
    '30-40%',
    '40%+'
  ], []);

  return (
    <BaseMap
      data={data}
      getFillColor={getFillColor}
      onHover={(e, geo, countyData) => handleMouseMove(e, geo, countyData)}
      legendTitle="Poverty Rate"
      legendColors={legendColors}
      legendValues={legendValues}
    />
  );
};

export default PovertyRateMap; 